import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Loader2, Check, Palette } from 'lucide-react';
import { ThemePreset, PRESENTATION_CREDITS } from '../../types';
import { useAuthStore } from '../../store/auth';

interface AIThemeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (theme: ThemePreset) => void;
  isDark: boolean;
  presentationId: string;
}

export default function AIThemeModal({
  isOpen,
  onClose,
  onApply,
  isDark,
  presentationId,
}: AIThemeModalProps) {
  const { token, user, checkAuth } = useAuthStore();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTheme, setGeneratedTheme] = useState<ThemePreset | null>(null);
  const [error, setError] = useState<string | null>(null);

  const API_URL = import.meta.env.VITE_API_URL || '/api';

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please describe the theme you want to generate');
      return;
    }

    if ((user?.credits_remaining || 0) < PRESENTATION_CREDITS.AI_THEME_GENERATION) {
      setError('Insufficient credits. You need 3 credits to generate a theme.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedTheme(null);

    try {
      const response = await fetch(`${API_URL}/presentations/${presentationId}/generate-theme`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: prompt.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Failed to generate theme');
      }

      const data = await response.json() as { theme: ThemePreset };
      setGeneratedTheme(data.theme);
      await checkAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate theme');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApply = () => {
    if (generatedTheme) {
      onApply(generatedTheme);
      onClose();
    }
  };

  const handleClose = () => {
    setPrompt('');
    setGeneratedTheme(null);
    setError(null);
    onClose();
  };

  const examplePrompts = [
    'Ocean sunset with warm orange and cool blue tones',
    'Professional tech startup with modern gradients',
    'Vintage newspaper with sepia and cream colors',
    'Neon cyberpunk with pink and cyan accents',
    'Nature-inspired forest theme with earthy greens',
    'Luxury brand with gold and deep navy',
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-lg rounded-2xl overflow-hidden ${
              isDark ? 'bg-dark-800' : 'bg-white'
            }`}
          >
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${
              isDark ? 'border-dark-700' : 'border-slate-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Generate AI Theme
                  </h2>
                  <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    Describe your ideal theme and let AI create it
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Credit info */}
              <div className={`p-3 rounded-xl flex items-center justify-between ${
                isDark ? 'bg-dark-700' : 'bg-slate-50'
              }`}>
                <span className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Cost: {PRESENTATION_CREDITS.AI_THEME_GENERATION} credits
                </span>
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Your balance: {user?.credits_remaining || 0} credits
                </span>
              </div>

              {/* Prompt input */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDark ? 'text-text-secondary' : 'text-slate-700'
                }`}>
                  Describe your theme
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Modern minimalist with soft purple accents and clean white background..."
                  rows={3}
                  className={`w-full px-4 py-3 rounded-xl border resize-none ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                      : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                />
              </div>

              {/* Example prompts */}
              <div>
                <p className={`text-xs mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Try these examples:
                </p>
                <div className="flex flex-wrap gap-2">
                  {examplePrompts.slice(0, 3).map((example, i) => (
                    <button
                      key={i}
                      onClick={() => setPrompt(example)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        isDark
                          ? 'bg-dark-700 text-text-secondary hover:bg-dark-600'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {example.length > 35 ? example.slice(0, 35) + '...' : example}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                  {error}
                </div>
              )}

              {/* Generated Theme Preview */}
              {generatedTheme && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl border ${
                    isDark ? 'border-dark-600' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Theme Generated: {generatedTheme.name}
                    </span>
                  </div>

                  {/* Theme Preview */}
                  <div
                    className="rounded-lg p-4 mb-3"
                    style={{ background: generatedTheme.colors.background }}
                  >
                    <div
                      className="text-lg font-bold mb-2"
                      style={{ color: generatedTheme.colors.primary }}
                    >
                      Sample Title
                    </div>
                    <div
                      className="text-sm mb-2"
                      style={{ color: generatedTheme.colors.text }}
                    >
                      This is sample body text
                    </div>
                    <div className="flex gap-2">
                      <div
                        className="w-8 h-8 rounded"
                        style={{ background: generatedTheme.colors.primary }}
                        title="Primary"
                      />
                      <div
                        className="w-8 h-8 rounded"
                        style={{ background: generatedTheme.colors.secondary }}
                        title="Secondary"
                      />
                      <div
                        className="w-8 h-8 rounded"
                        style={{ background: generatedTheme.colors.accent }}
                        title="Accent"
                      />
                      <div
                        className="w-8 h-8 rounded border"
                        style={{
                          background: generatedTheme.colors.text,
                          borderColor: isDark ? '#374151' : '#e2e8f0'
                        }}
                        title="Text"
                      />
                    </div>
                  </div>

                  <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    {generatedTheme.description}
                  </p>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className={`p-4 border-t flex gap-3 ${
              isDark ? 'border-dark-700' : 'border-slate-200'
            }`}>
              <button
                onClick={handleClose}
                className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${
                  isDark
                    ? 'bg-dark-700 text-white hover:bg-dark-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Cancel
              </button>
              {generatedTheme ? (
                <button
                  onClick={handleApply}
                  className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Palette className="w-4 h-4" />
                  Apply Theme
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim() || (user?.credits_remaining || 0) < PRESENTATION_CREDITS.AI_THEME_GENERATION}
                  className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate ({PRESENTATION_CREDITS.AI_THEME_GENERATION} credits)
                    </>
                  )}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
